/**
 * Realm roles allowed to approve in MEMBER_QUORUM (Innova).
 * Keep in sync with ms-application MemberQuorumApprovalStrategy.COMMITTEE_MEMBER_ROLE_ALIASES.
 */
export const COMMITTEE_MEMBER_REALM_ROLES = ['COMITE', 'CMT'] as const;

export type CommitteeMemberRealmRole =
  (typeof COMMITTEE_MEMBER_REALM_ROLES)[number];

export function hasCommitteeMemberRealmRole(roles: string[]): boolean {
  return roles.some((role) =>
    (COMMITTEE_MEMBER_REALM_ROLES as readonly string[]).includes(role),
  );
}

export type CommitteeViewerRoleFlags = {
  isComite: boolean;
  isCommercial: boolean;
  isInstrumentator: boolean;
  isAdmin: boolean;
  isRiskAnalyst: boolean;
  isCommitteeOnly?: boolean;
};

/** Roles that may open committee folder detail (read-only or with actions). */
export function canViewCommitteeDetail(flags: CommitteeViewerRoleFlags): boolean {
  return (
    flags.isComite ||
    flags.isCommercial ||
    flags.isInstrumentator ||
    flags.isAdmin ||
    flags.isRiskAnalyst ||
    !!flags.isCommitteeOnly
  );
}

/**
 * Roles that may trigger committee approval modals/actions (approve / send to INS).
 * MEMBER_QUORUM (Innova): COMITE/CMT members and ADMIN approve; pure ARQ is view + decline only.
 */
export function canPerformCommitteeApprovalActions(
  isMemberQuorumMode: boolean,
  tokenRoles: string[],
  flags: CommitteeViewerRoleFlags,
): boolean {
  if (flags.isAdmin) {
    return true;
  }
  if (flags.isRiskAnalyst) {
    return false;
  }
  if (isMemberQuorumMode) {
    return hasCommitteeMemberRealmRole(tokenRoles);
  }
  return (
    flags.isComite ||
    flags.isCommercial ||
    flags.isInstrumentator ||
    !!flags.isCommitteeOnly
  );
}

/**
 * Roles that may decline or request changes in committee (REJ / PEN).
 * Pure ANALISTA_RIESGO may decline but not approve (Task 51835).
 */
export function canPerformCommitteeDeclineActions(
  isMemberQuorumMode: boolean,
  tokenRoles: string[],
  flags: CommitteeViewerRoleFlags,
): boolean {
  if (flags.isAdmin) {
    return true;
  }
  if (flags.isRiskAnalyst) {
    return true;
  }
  if (isMemberQuorumMode) {
    return hasCommitteeMemberRealmRole(tokenRoles);
  }
  return (
    flags.isComite ||
    flags.isCommercial ||
    flags.isInstrumentator ||
    !!flags.isCommitteeOnly
  );
}

export type CommitteeActionButtonVisibilityInput = {
  isMultipleApprovalInCommitteeEnabled: boolean;
  isCommitteeMemberQuorumEnabled: boolean;
  canApprove: boolean;
  canPerformCommitteeDecline: boolean;
};

/** Approve / send-to-INS buttons: gated by current approval step or quorum slot. */
export function shouldShowCommitteeApprovalButtons(
  input: CommitteeActionButtonVisibilityInput,
): boolean {
  if (
    !input.isMultipleApprovalInCommitteeEnabled &&
    !input.isCommitteeMemberQuorumEnabled
  ) {
    return true;
  }
  return input.canApprove;
}

/**
 * Decline / request-changes button: ARQ may act without `canApprove` (Task 51835).
 */
export function shouldShowCommitteeDeclineButtons(
  input: CommitteeActionButtonVisibilityInput,
): boolean {
  if (
    !input.isMultipleApprovalInCommitteeEnabled &&
    !input.isCommitteeMemberQuorumEnabled
  ) {
    return true;
  }
  return input.canApprove || input.canPerformCommitteeDecline;
}

/**
 * MEMBER_QUORUM: ocultar "Declinar" (REJ) salvo ADMIN o ANALISTA_RIESGO (Task 51835).
 * Otros roles solo ven "Solicitar cambios" (PEN). LEGACY: siempre false.
 */
export function shouldHideCommitteeDeclineOption(
  isMemberQuorumMode: boolean,
  isAdmin: boolean,
  isRiskAnalyst: boolean,
): boolean {
  return isMemberQuorumMode && !isAdmin && !isRiskAnalyst;
}
